import React, { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Spin, message } from 'antd';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import doNothing from '../../../../../helpers/doNothing';
import queries from '../../../queries/queries';
import deleteRoom from '../../../requests/deleteRoom';
import FooterButtons from '../FooterButtons/FooterButtons';
import RoomForm from './components/RoomForm';
import { transformRoomFormValues } from './utils/transformRoomFormValues';
import useCreateRoomMutation from '../../../mutations/useCreateRoomMutation';
import useAddRoomAttachmentsMutation from '../../../mutations/useAddRoomAttachmentsMutation';
import useAddRoomImagesMutation from '../../../mutations/useAddRoomImagesMutation';
import useEditRoomMutation from '../../../mutations/useEditRoomMutation';

const { confirm } = Modal;

const RoomModalNode = ({ visible, onOk, onCancel, form, editingRoom }) => {
  const userCanDelete = useSelector(
    (store) => store.authentication.userInfo.CanDeleteOffice
  );

  const params = useParams();

  const history = useHistory();

  const queryClient = useQueryClient();

  const [fileList, setFileList] = useState([]);
  const [imagesList, setImagesList] = useState([]);
  const [numberError, setNumberError] = useState(false);

  const createAndEditRoomError = useCallback(
    (msg) => {
      if (msg === 'Введенный вами номер уже используется') {
        setNumberError(true);
      } else {
        message.error('При создании помещения произошла ошибка');
      }
    },
    [setNumberError]
  );

  const { mutateAsync: addRoomAttachmentsMutateAsync } =
    useAddRoomAttachmentsMutation();

  const { mutateAsync: addRoomImagesMutateAsync } = useAddRoomImagesMutation();

  const { mutate: createRoomMutate, isLoading: isCreateRoomLoading } =
    useCreateRoomMutation({
      onSuccess: async (res) => {
        const data = res?.data;

        if (data?.result === 'Error') {
          createAndEditRoomError(data.message);
        } else {
          if (data?.id && fileList.length) {
            await addRoomAttachmentsMutateAsync({
              roomId: data.id,
              files: fileList,
            });

            setFileList([]);
          }

          if (data.description?.id && imagesList.length) {
            await addRoomImagesMutateAsync({
              descriptionId: data.description.id,
              files: imagesList,
            });

            setImagesList([]);
          }

          queryClient.invalidateQueries(queries.roomsInFacility);
          form.resetFields();
          onOk();
        }
      },
    });

  const { mutate: editRoomMutate, isLoading: isEditRoomLoading } =
    useEditRoomMutation({
      onSuccess: async (res) => {
        const data = res?.data;

        if (data?.result === 'Error') {
          createAndEditRoomError(data.message);
        } else {
          const newFiles = fileList.filter((file) => file instanceof File);
          const newImages = imagesList.filter((image) => image instanceof File);

          if (newFiles.length > 0) {
            await addRoomAttachmentsMutateAsync({
              roomId: editingRoom.id,
              files: newFiles,
            });

            setFileList([]);
          }

          if (newImages.length > 0) {
            await addRoomImagesMutateAsync({
              descriptionId: editingRoom.description.id,
              files: newImages,
            });

            setImagesList([]);
          }

          queryClient.invalidateQueries(queries.room);
          form.resetFields();
          onOk();
        }
      },
    });

  useEffect(() => {
    if (editingRoom) {
      setFileList(
        editingRoom.attachments?.map((file) => {
          file.uid = file.id;
          return file;
        }) || []
      );
      setImagesList(
        editingRoom.description?.attachments?.map((file) => {
          file.uid = file.id;
          return file;
        }) || []
      );
    }
  }, [editingRoom]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      form.validateFieldsAndScroll(async (err, values) => {
        if (!err) {
          const data = transformRoomFormValues(values);

          if (!editingRoom) {
            createRoomMutate({
              facilityId: params.facilityId,
              formValues: data,
            });
          } else {
            const editRoomData = {
              ...data,
              id: editingRoom.id,
              attachments: fileList.filter((file) => file.id),
              description: {
                id: editingRoom.description.id,
                description: data.description,
                attachments: imagesList.filter((image) => image.id),
              },
            };

            editRoomMutate({ formValues: editRoomData });
          }
        }
      });
    },
    [
      editingRoom,
      createRoomMutate,
      params.facilityId,
      fileList,
      imagesList,
      editRoomMutate,
    ]
  );

  const onDelete = useCallback(() => {
    confirm({
      title: `Вы уверены, что хотите удалить помещение ${editingRoom.name}?`,
      content: 'Это действие нельзя будет отменить',
      onOk: async () => {
        await deleteRoom(editingRoom.id);
        history.push('/Offices/facilities');
        // onCancel();
      },
      onCancel() {},
    });
  }, [editingRoom]);

  return (
    <Modal
      title={`${editingRoom ? 'Редактировать' : 'Добавить'} помещение`}
      okText="Сохранить"
      visible={visible}
      onCancel={isCreateRoomLoading || isEditRoomLoading ? doNothing : onCancel}
      width={650}
      maskClosable={false}
      footer={
        <FooterButtons
          disabled={isCreateRoomLoading || isEditRoomLoading}
          onOk={handleSubmit}
          onCancel={onCancel}
          onDelete={onDelete}
          deleteButtonText="Удалить помещение"
          showDelete={editingRoom && userCanDelete}
        />
      }
    >
      <Spin spinning={isCreateRoomLoading || isEditRoomLoading}>
        <RoomForm
          form={form}
          fileList={fileList}
          setFileList={setFileList}
          imagesList={imagesList}
          setImagesList={setImagesList}
          editingRoom={editingRoom}
          numberError={numberError}
        />
      </Spin>
    </Modal>
  );
};

const RoomModal = Form.create({ name: 'add_new_room' })(RoomModalNode);

export default RoomModal;
